export const FOLDER_MOVE_ERROR_I18N_KEYS = {
  LINKED_RESOURCE_REQUIRES_SAME_AREA:
    'com.streamlain.rootAppSidebar.organize.folder.error.move-linked-resources-same-area',
  FOLDER_MOVE_PRIVATE_OWNER_REQUIRED:
    'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-private-owner-required',
  FOLDER_MOVE_ROLE_ESCALATION_DENIED:
    'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-role-escalation-denied',
  FOLDER_MOVE_SOURCE_DENIED:
    'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-source-denied',
  FOLDER_MOVE_TARGET_DENIED:
    'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied',
} as const;

export type FolderMoveErrorI18nKey =
  | (typeof FOLDER_MOVE_ERROR_I18N_KEYS)[keyof typeof FOLDER_MOVE_ERROR_I18N_KEYS]
  | 'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
  | 'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-failed';

export function folderMoveErrorI18nKey(
  error: unknown,
  fallback: Extract<
    FolderMoveErrorI18nKey,
    | 'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
    | 'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-failed'
  > = 'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-failed'
): FolderMoveErrorI18nKey {
  const code = folderMoveErrorCode(error);
  return code && code in FOLDER_MOVE_ERROR_I18N_KEYS
    ? FOLDER_MOVE_ERROR_I18N_KEYS[
        code as keyof typeof FOLDER_MOVE_ERROR_I18N_KEYS
      ]
    : fallback;
}

function folderMoveErrorCode(error: unknown): string | null {
  const directCode = readStringProperty(error, 'code');
  if (directCode) {
    return directCode;
  }

  const extensions = readObjectProperty(error, 'extensions');
  const extensionCode = readStringProperty(extensions, 'code');
  if (extensionCode) {
    return extensionCode;
  }

  const graphQLErrors = readArrayProperty(error, 'graphQLErrors');
  const firstGraphQLErrorCode = readStringProperty(
    readObjectProperty(graphQLErrors?.[0], 'extensions'),
    'code'
  );
  if (firstGraphQLErrorCode) {
    return firstGraphQLErrorCode;
  }

  const errors = readArrayProperty(error, 'errors');
  return (
    readStringProperty(readObjectProperty(errors?.[0], 'extensions'), 'code') ??
    null
  );
}

function readObjectProperty(
  value: unknown,
  property: string
): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || !(property in value)) {
    return null;
  }
  const nested = (value as Record<string, unknown>)[property];
  return typeof nested === 'object' && nested !== null
    ? (nested as Record<string, unknown>)
    : null;
}

function readArrayProperty(value: unknown, property: string): unknown[] | null {
  if (typeof value !== 'object' || value === null || !(property in value)) {
    return null;
  }
  const nested = (value as Record<string, unknown>)[property];
  return Array.isArray(nested) ? nested : null;
}

function readStringProperty(value: unknown, property: string): string | null {
  if (typeof value !== 'object' || value === null || !(property in value)) {
    return null;
  }
  const nested = (value as Record<string, unknown>)[property];
  return typeof nested === 'string' && nested.length > 0 ? nested : null;
}
